import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Plus } from "lucide-react";
import { toast } from "sonner";
import { MENU, BUSINESS, DEAL_CHOICES } from "../data/site";
import { useCart } from "../context/CartContext";
import DealModal from "./DealModal";
import { Reveal, ClipReveal, Chapter, EASE } from "./Reveal";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

export default function MenuSection() {
  const [active, setActive] = useState(MENU[0].id);
  const { add, setOpen } = useCart();
  const category = MENU.find((c) => c.id === active);

  const [dealItem, setDealItem] = useState(null);

  const handleAdd = (item) => {
    if (DEAL_CHOICES[item.name] || item.variants) {
      setDealItem(item);
      return;
    }
    add({ id: `${category.id}-${slug(item.name)}`, name: item.name, price: item.price });
    toast.success(`${item.name} added to your order`, {
      action: { label: "View", onClick: () => setOpen(true) },
    });
  };

  const handleDealAdd = (item, selections, price) => {
    add({
      id: `custom-${slug(item.name)}-${Date.now()}`,
      name: item.name,
      price: price || item.price,
      selections,
    });
    setDealItem(null);
    toast.success(`${item.name} added to your order`, {
      action: { label: "View", onClick: () => setOpen(true) },
    });
  };

  return (
    <section id="menu" data-testid="menu-section" className="mx-auto max-w-7xl px-5 py-24 md:px-10 md:py-36">
      <Chapter number="01" title="The Menu" />
      <div className="mt-6 flex flex-col gap-10 md:flex-row md:items-end md:justify-between">
        <Reveal delay={0.05}>
          <h2 className="max-w-xl font-serif text-4xl font-bold leading-tight tracking-tight md:text-6xl">
            Coffee, burgers &amp; <span className="italic text-ember">Roman-style pizza</span>
          </h2>
        </Reveal>
        <Reveal delay={0.15} className="flex flex-col gap-5 md:items-end">
          <p className="max-w-sm text-sm text-charcoal/60 md:text-base">
            Open Mon–Fri 7:00 AM – 8:00 PM · Roman-style pizza from 2 PM · Burgers made to order with fresh mince &amp; fresh chicken.
          </p>
          <a
            data-testid="menu-call-to-order-button"
            href={BUSINESS.phoneHref}
            className="group flex w-fit items-center gap-3 rounded-full bg-ember px-7 py-3.5 text-xs font-bold uppercase tracking-widest text-cream transition-transform duration-300 hover:scale-105 hover:bg-[#991B1B]"
          >
            <Phone size={14} className="transition-transform duration-300 group-hover:rotate-12" />
            Call to Order — {BUSINESS.phoneDisplay}
          </a>
        </Reveal>
      </div>

      <Reveal delay={0.1} className="mt-14">
        <div data-testid="menu-tabs" className="flex gap-2 overflow-x-auto pb-2 md:flex-wrap">
          {MENU.map((c) => (
            <button
              key={c.id}
              data-testid={`menu-tab-${c.id}`}
              onClick={() => setActive(c.id)}
              className={`relative whitespace-nowrap rounded-full border px-6 py-3 text-xs font-bold uppercase tracking-widest transition-colors duration-300 ${
                active === c.id
                  ? "border-ember text-cream"
                  : "border-charcoal/20 bg-transparent text-charcoal/60 hover:border-ember hover:text-ember"
              }`}
            >
              {active === c.id && (
                <motion.span
                  layoutId="menu-tab-pill"
                  className="absolute inset-0 rounded-full bg-ember"
                  transition={{ type: "spring", stiffness: 350, damping: 30 }}
                />
              )}
              <span className="relative z-10">{c.label}</span>
            </button>
          ))}
        </div>
      </Reveal>

      <div className="mt-12 grid gap-12 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-28">
            <AnimatePresence mode="wait">
              <motion.div
                key={category.id}
                initial={{ clipPath: "inset(0 0 100% 0)" }}
                animate={{ clipPath: "inset(0 0 0% 0)" }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.7, ease: EASE }}
                className="group relative aspect-[3/4] max-h-[70vh] w-full overflow-hidden"
              >
                <img src={category.image} alt={category.label} className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-charcoal/70 via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 p-6">
                  <p className="font-display text-3xl uppercase tracking-wide text-cream md:text-4xl">{category.label}</p>
                  <p className="mt-1 text-xs font-semibold uppercase tracking-[0.3em] text-gold">{category.note}</p>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        <div className="lg:col-span-7">
          <AnimatePresence mode="wait">
            <motion.ul
              key={category.id}
              initial="hidden"
              animate="show"
              exit={{ opacity: 0, transition: { duration: 0.15 } }}
              variants={{ show: { transition: { staggerChildren: 0.06 } } }}
              className="divide-y divide-charcoal/10"
              data-testid={`menu-list-${category.id}`}
            >
              {category.items.map((item) => (
                <motion.li
                  key={item.name}
                  variants={{ hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: EASE } } }}
                  className="group py-6"
                  data-testid={`menu-item-${slug(item.name)}`}
                >
                  <div className="flex items-end">
                    <h3 className="font-serif text-xl font-semibold transition-colors duration-300 group-hover:text-ember md:text-2xl">
                      {item.name}
                    </h3>
                    {item.tags?.map((t) => (
                      <span
                        key={t}
                        data-testid={`tag-${slug(item.name)}-${t.toLowerCase()}`}
                        className={`mb-0.5 ml-2 shrink-0 border px-1.5 py-0.5 text-[0.55rem] font-bold tracking-widest ${
                          t === "HOT" ? "border-ember/50 text-ember" : "border-green-700/50 text-green-700"
                        }`}
                      >
                        {t}
                      </span>
                    ))}
                    <span className="menu-leader" />
                    <span className="font-display text-xl text-ember md:text-2xl">${item.price}</span>
                  </div>
                  <div className="mt-2 flex items-start justify-between gap-6">
                    <p className="max-w-md text-sm text-charcoal/60">{item.desc}</p>
                    <button
                      data-testid={`menu-add-${slug(item.name)}`}
                      onClick={() => handleAdd(item)}
                      aria-label={`Add ${item.name} to order`}
                      className="flex shrink-0 items-center gap-1.5 rounded-full border border-charcoal/20 px-4 py-2 text-[0.65rem] font-bold uppercase tracking-widest text-charcoal/60 transition-all duration-300 hover:border-ember hover:bg-ember hover:text-cream"
                    >
                      <Plus size={11} /> Add
                    </button>
                  </div>
                </motion.li>
              ))}
            </motion.ul>
          </AnimatePresence>
        </div>
      </div>
      <AnimatePresence>
        {dealItem && (
          <DealModal key={dealItem.name} item={dealItem} onClose={() => setDealItem(null)} onAdd={handleDealAdd} />
        )}
      </AnimatePresence>
    </section>
  );
}
